import * as Sentry from "@sentry/react";

enum BadgeLoggingErrorPatterns {
  DESTRUCTURE_BADGE_INFO = "Cannot destructure property 'badgeInfo'",
  READ_PROPERTIES_UNDEFINED = "Cannot read properties of undefined",
  READ_PROPERTIES_NULL = "Cannot read properties of null",
  DESTRUCTURE_KEYWORD = "destructure",
  BADGE_INFO_KEYWORD = "badgeInfo"
}

enum PrintServerErrorKeywords {
  UNABLE_TO_CONNECT = "Unable to connect to Print Server",
  PRINT_SERVER = "Print Server",
  JSPRINTMANAGER = "JSPrintManager",
  JSPM = "JSPM",
  WEBSOCKET_STATUS = "websocket_status",
  WEBSOCKET_CONNECTION_FAILED = "WebSocket connection failed"
}

/**
 * Global error handler for unhandled promise rejections and DOM events
 * Specifically handles WebSocket connection errors and print server issues
 */
class GlobalErrorHandler {
  private static instance: GlobalErrorHandler;
  private isInitialized = false;

  private constructor() {}

  public static getInstance(): GlobalErrorHandler {
    if (!GlobalErrorHandler.instance) {
      GlobalErrorHandler.instance = new GlobalErrorHandler();
    }
    return GlobalErrorHandler.instance;
  }

  /**
   * Initialize global error handlers
   */
  public init(): void {
    if (this.isInitialized) {
      return;
    }

    this.setupUnhandledRejectionHandler();
    this.setupErrorHandler();
    this.isInitialized = true;
  }

  /**
   * Setup handler for unhandled promise rejections
   * Public for testing purposes
   */
  public setupUnhandledRejectionHandler(): void {
    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      const error = event.reason;
      
      // First check: if this is a raw Event object, prevent it and don't report
      // This should never happen - safety net
      if (error instanceof Event || error?.constructor?.name === 'Event') {
        console.warn('Unhandled rejection with Event object detected (should not happen):', error);
        event.preventDefault();
        return;
      }
      
      // Check if this is a destructuring error from logBadgePrintFailEvent
      if (this.isLogBadgePrintFailEventError(error)) {
        // Prevent the error from being logged as unhandled
        event.preventDefault();
        
        // Report to Sentry with structured context
        this.reportStructuredError('badge_logging_destructuring_error', {
          errorType: 'badge_logging_destructuring_error',
          errorMessage: error?.message || 'Destructuring error in badge logging',
          recommendation: 'Check badge state passed to logBadgePrintFailEvent',
          handled: true
        });
        return;
      }
      
      // Check if this is a WebSocket or print server related error
      if (this.isPrintServerError(error) || this.isWebSocketError(error)) {
        // Prevent the error from being logged as unhandled
        // These are expected operational failures, not bugs, so we don't send to Sentry
        console.debug('Print server connection check failed (expected):', error?.message);
        event.preventDefault();
        return;
      }

      // For other unhandled rejections, let them be handled normally by Sentry
      // (don't prevent default behavior)
    });
  }

  /**
   * Handle uncaught errors
   */
  private setupErrorHandler(): void {
    window.addEventListener('error', (event: ErrorEvent) => {
      // Check if this is related to print server
      if (this.isPrintServerError(event.error) || 
          event.message?.includes('Print Server') ||
          event.message?.includes('WebSocket')) {
        
        // These are expected operational failures, log but don't send to Sentry
        console.debug('Print server error (expected):', event.error?.message || event.message);

        return true; // Prevent default browser error handling
      }

      return false; // Allow default handling for other errors
    });
  }

  /**
   * Check if this is a destructuring error from logBadgePrintFailEvent
   */
  private isLogBadgePrintFailEventError(error: any): boolean {
    if (!error) return false;
    
    const errorMessage = error.message || error.toString();
    
    // Check for specific destructuring errors from the badge logging function
    return errorMessage.includes(BadgeLoggingErrorPatterns.DESTRUCTURE_BADGE_INFO) ||
           errorMessage.includes(BadgeLoggingErrorPatterns.READ_PROPERTIES_UNDEFINED) ||
           errorMessage.includes(BadgeLoggingErrorPatterns.READ_PROPERTIES_NULL) ||
           (errorMessage.includes(BadgeLoggingErrorPatterns.DESTRUCTURE_KEYWORD) && 
            errorMessage.includes(BadgeLoggingErrorPatterns.BADGE_INFO_KEYWORD));
  }

  /**
   * Check if error is related to print server
   */
  private isPrintServerError(error: any): boolean {
    if (!error) return false;
    
    const errorMessage = error.message || error.toString();
    const printServerKeywords = [
      PrintServerErrorKeywords.UNABLE_TO_CONNECT,
      PrintServerErrorKeywords.PRINT_SERVER,
      PrintServerErrorKeywords.JSPRINTMANAGER,
      PrintServerErrorKeywords.JSPM,
      PrintServerErrorKeywords.WEBSOCKET_STATUS,
      PrintServerErrorKeywords.WEBSOCKET_CONNECTION_FAILED
    ];

    return printServerKeywords.some(keyword => 
      errorMessage.toLowerCase().includes(keyword.toLowerCase())
    );
  }

  /**
   * Check if error is related to WebSocket connection
   */
  private isWebSocketError(error: any): boolean {
    if (!error) return false;

    // Check if the error is a WebSocket-related error
    return error instanceof Error && (
      error.message?.includes('WebSocket') ||
      error.message?.includes('connection') ||
      error.constructor?.name === 'WebSocket' ||
      ((error as any).target && (error as any).target.constructor?.name === 'WebSocket')
    );
  }





  /**
   * Handle print server connection failures gracefully
   * These are expected operational failures, so we log them but don't send to Sentry
   */
  public handlePrintServerError(error: Error, operation?: string): void {
    console.debug(`Print server operation failed (${operation || 'unknown'}):`, error.message);
    // Note: Not sending to Sentry as these are expected when print server is unavailable
  }

  /**
   * Enhanced badge printing error handler with direct categorization
   */
  public handleBadgePrintingError(error: Error, context: {
    operation: string;
    printMethod?: string;
    printerName?: string;
    badgeId?: string;
    paperSize?: string;
    fileFormat?: string;
    serverConnected?: boolean;
    correlationId?: string;
  }): void {
    // Categorize the error for better Sentry reporting
    const categorization = this.categorizeError(error, context);
    
    const errorDetails = {
      errorType: 'badge_printing_error',
      timestamp: new Date().toISOString(),
      operation: context.operation,
      printMethod: context.printMethod || 'unknown',
      printerName: context.printerName || 'default',
      badgeId: context.badgeId || 'unknown',
      paperSize: context.paperSize || 'unknown',
      fileFormat: context.fileFormat || 'unknown',
      serverConnected: context.serverConnected || false,
      errorMessage: error.message,
      errorStack: error.stack,
      // Enhanced categorization for Sentry
      errorCategory: categorization.category,
      severity: categorization.severity,
      recommendations: categorization.recommendations,
      handled: true,
      correlationId: context.correlationId
    };

    // Send enhanced error to Sentry
    this.reportStructuredError('badge_printing_failure', errorDetails);
  }

  /**
   * Categorize badge printing errors for better diagnostics
   */
  private categorizeError(error: Error, context: any): {
    category: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
    recommendations: string[];
  } {
    const errorMessage = error.message.toLowerCase();
    
    // Connection errors
    if (errorMessage.includes('websocket') || 
        errorMessage.includes('connection') || 
        errorMessage.includes('network') ||
        context.serverConnected === false) {
      return {
        category: 'connection_error',
        severity: 'high',
        recommendations: [
          'Check if JSPrintManager service is running',
          'Verify network connectivity',
          'Restart JSPrintManager service if needed'
        ]
      };
    }

    // Printer specific errors
    if (errorMessage.includes('printer') || 
        errorMessage.includes('device') ||
        errorMessage.includes('not ready') ||
        errorMessage.includes('offline')) {
      return {
        category: 'printer_error',
        severity: 'medium',
        recommendations: [
          `Check if printer '${context.printerName || 'default'}' is online`,
          'Verify printer has paper and is not jammed',
          'Check printer driver installation'
        ]
      };
    }

    // Format/file errors
    if (errorMessage.includes('format') || 
        errorMessage.includes('file') ||
        errorMessage.includes('invalid') ||
        errorMessage.includes('unsupported')) {
      return {
        category: 'format_error',
        severity: 'low',
        recommendations: [
          `Check file format '${context.fileFormat || 'unknown'}' is supported`,
          'Verify badge generation completed successfully',
          'Try regenerating the badge'
        ]
      };
    }

    // Server/service errors
    if (errorMessage.includes('server') || 
        errorMessage.includes('service') ||
        errorMessage.includes('unavailable')) {
      return {
        category: 'server_error',
        severity: 'critical',
        recommendations: [
          'Check JSPrintManager server status',
          'Verify print server configuration',
          'Contact system administrator if issue persists'
        ]
      };
    }

    // Default categorization
    return {
      category: 'unknown_error',
      severity: 'medium',
      recommendations: [
        'Check browser console for additional details',
        'Try refreshing the page and printing again',
        'Contact support if issue persists'
      ]
    };
  }

  /**
   * Record successful badge printing (simple logging)
   */
  public recordBadgePrintingSuccess(context: {
    operation: string;
    printMethod?: string;
    printerName?: string;
    badgeId?: string;
    paperSize?: string;
    fileFormat?: string;
    correlationId?: string;
  }): void {
    // For success events, we can just send a simple structured event to Sentry for analytics
    this.reportStructuredError('badge_printing_success', {
      ...context,
      timestamp: new Date().toISOString(),
      result: 'success'
    });
  }

  /**
   * Check if Sentry is initialized
   */
  private isSentryEnabled(): boolean {
    const env = process.env.REACT_APP_ENVIRONMENT;
    return env === 'prod' || env === 'preprod';
  }

  /**
   * Report structured errors for monitoring
   */
  private reportStructuredError(errorType: string, details: any): void {
    // Only send to Sentry if it's enabled for this environment
    if (!this.isSentryEnabled()) {
      return;
    }

    // Send to Sentry with structured tags and context
    Sentry.withScope((scope) => {
      scope.setTag('error_type', errorType);
      scope.setTag('handled', 'yes');
      scope.setTag('component', 'badge_printing');
      
      if (details.printMethod) scope.setTag('print_method', details.printMethod);
      if (details.printerName) scope.setTag('printer_name', details.printerName);
      if (details.errorCategory) scope.setTag('error_category', details.errorCategory);
      if (details.severity) scope.setTag('severity', details.severity);
      if (details.correlationId) scope.setTag('correlation_id', details.correlationId);
      
      scope.setContext('error_details', details);
      
      // For success events, use a different Sentry method
      if (errorType.includes('success')) {
        Sentry.addBreadcrumb({
          message: `Badge printing succeeded: ${details.operation}`,
          level: 'info',
          category: 'badge_printing',
          data: details
        });
      } else {
        Sentry.captureMessage(`${errorType}: ${details.errorMessage || errorType}`, 'error');
      }
    });
  }
}

export default GlobalErrorHandler;
